/**
 * useAgentsFlowApi Hook
 * Custom hook for API operations with the AgentsFlow context
 */

import { useCallback } from 'react';
import { useAgentsFlow } from '../contexts/AgentsFlowContext';
import AgentsFlowService from '../services/AgentsFlowService';

export function useAgentsFlowApi() {
  const {
    state,
    setAgents,
    setAgentsLoading,
    setAgentsError,
    addAgent,
    updateAgent: updateAgentState,
    removeAgent,
    setCurrentAgent,
    setTemplates,
    setTemplatesLoading,
    setSaving,
    setPublishing,
    setError,
    clearError,
    setEditorData,
    addSimulatorMessage,
    setSimulatorTyping,
    setSuccessModal,
  } = useAgentsFlow();

  // ===============================================
  // AGENTS
  // ===============================================

  /**
   * Load all agents
   */
  const loadAgents = useCallback(async (params = {}) => {
    setAgentsLoading(true);
    clearError();
    try {
      const response = await AgentsFlowService.listAgents(params);
      setAgents(response.data?.results || response.data || []);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar agentes';
      setAgentsError(errorMsg);
      throw error;
    }
  }, [setAgents, setAgentsLoading, setAgentsError, clearError]);

  /**
   * Load single agent by ID
   */
  const loadAgent = useCallback(async (id) => {
    setAgentsLoading(true);
    clearError();
    try {
      const response = await AgentsFlowService.getAgent(id);
      const agentData = response.data;
      setCurrentAgent(agentData);

      // Also populate editor state with agent data
      setEditorData({
        identity_config: agentData.identity_config || {},
        business_info: agentData.business_info || {},
        behavior_config: agentData.behavior_config || {},
        humanization_config: agentData.humanization_config || {},
        sales_config: agentData.sales_config || {},
        agent_contacts: agentData.agent_contacts || [],
        llm_config: agentData.llm_config || {},
        custom_messages: agentData.custom_messages || {},
        capabilities: agentData.capabilities || [],
      });

      return agentData;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar agente';
      setError(errorMsg);
      throw error;
    } finally {
      setAgentsLoading(false);
    }
  }, [setCurrentAgent, setAgentsLoading, setError, clearError, setEditorData]);

  /**
   * Create new agent (full creation)
   */
  const createAgent = useCallback(async (data) => {
    setSaving(true);
    clearError();
    try {
      const response = await AgentsFlowService.createAgent(data);
      addAgent(response.data);
      setCurrentAgent(response.data);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao criar agente';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [addAgent, setCurrentAgent, setSaving, setError, clearError]);

  /**
   * Quick create agent (Level 1)
   */
  const quickCreateAgent = useCallback(async (wizardData) => {
    setSaving(true);
    clearError();
    try {
      const payload = {
        template_sector: wizardData.businessType,
        business_info: wizardData.businessInfo,
        capabilities: wizardData.selectedCapabilities,
        experience_level: 1, // Level 1: Quick
      };
      const response = await AgentsFlowService.quickCreateAgent(payload);
      addAgent(response.data);
      setCurrentAgent(response.data);
      setSuccessModal(true);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao criar agente';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [addAgent, setCurrentAgent, setSaving, setError, clearError, setSuccessModal]);

  /**
   * Update existing agent
   */
  const updateAgentApi = useCallback(async (id, data) => {
    setSaving(true);
    clearError();
    try {
      const response = await AgentsFlowService.updateAgent(id, data);
      updateAgentState(response.data);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao atualizar agente';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [updateAgentState, setSaving, setError, clearError]);

  /**
   * Delete agent
   */
  const deleteAgent = useCallback(async (id) => {
    clearError();
    try {
      await AgentsFlowService.deleteAgent(id);
      removeAgent(id);
      if (state.currentAgent?.id === id) {
        setCurrentAgent(null);
      }
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao excluir agente';
      setError(errorMsg);
      throw error;
    }
  }, [removeAgent, setCurrentAgent, setError, clearError, state.currentAgent]);

  /**
   * Publish/Activate agent
   */
  const publishAgent = useCallback(async (id) => {
    setPublishing(true);
    clearError();
    try {
      const response = await AgentsFlowService.publishAgent(id);
      updateAgentState({ id, is_active: true, ...response.data });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao publicar agente';
      setError(errorMsg);
      throw error;
    } finally {
      setPublishing(false);
    }
  }, [updateAgentState, setPublishing, setError, clearError]);

  // ===============================================
  // CAPABILITIES
  // ===============================================

  /**
   * Load agent capabilities
   */
  const loadCapabilities = useCallback(async (agentId) => {
    try {
      const response = await AgentsFlowService.getAgentCapabilities(agentId);
      setEditorData({ capabilities: response.data || [] });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar capacidades';
      setError(errorMsg);
      throw error;
    }
  }, [setEditorData, setError]);

  /**
   * Toggle capabilities
   */
  const toggleCapabilities = useCallback(async (agentId, capabilities) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.toggleCapabilities(agentId, { capabilities });
      setEditorData({ capabilities: response.data || [] });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao atualizar capacidades';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setEditorData, setError]);

  /**
   * Update capability configuration
   */
  const updateCapabilityConfig = useCallback(async (agentId, capabilityCode, config) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.updateCapabilityConfig(agentId, capabilityCode, config);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao atualizar configuração';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError]);

  // ===============================================
  // KNOWLEDGE BASE
  // ===============================================

  /**
   * Load knowledge base
   */
  const loadKnowledge = useCallback(async (agentId) => {
    try {
      const response = await AgentsFlowService.getKnowledge(agentId);
      setEditorData({ knowledge: response.data || [] });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar base de conhecimento';
      setError(errorMsg);
      throw error;
    }
  }, [setEditorData, setError]);

  /**
   * Upload document
   */
  const uploadDocument = useCallback(async (agentId, file) => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source_type', 'file');
      const response = await AgentsFlowService.uploadKnowledgeDocument(agentId, formData);
      // Reload knowledge after upload
      await loadKnowledge(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao enviar documento';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadKnowledge]);

  /**
   * Delete knowledge source
   */
  const deleteKnowledge = useCallback(async (agentId, knowledgeId) => {
    try {
      await AgentsFlowService.deleteKnowledge(agentId, knowledgeId);
      await loadKnowledge(agentId);
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao excluir fonte de conhecimento';
      setError(errorMsg);
      throw error;
    }
  }, [setError, loadKnowledge]);

  /**
   * Sync knowledge source
   */
  const syncKnowledge = useCallback(async (agentId, knowledgeId) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.syncKnowledge(agentId, knowledgeId);
      await loadKnowledge(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao sincronizar';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadKnowledge]);

  /**
   * Add website knowledge source
   */
  const addWebsiteKnowledge = useCallback(async (agentId, data) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.addWebsiteKnowledge(agentId, data);
      await loadKnowledge(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao adicionar website';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadKnowledge]);

  /**
   * Connect Google Sheets
   */
  const connectGoogleSheets = useCallback(async (agentId, data) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.connectGoogleSheets(agentId, data);
      await loadKnowledge(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao conectar Google Sheets';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadKnowledge]);

  /**
   * Connect Notion
   */
  const connectNotion = useCallback(async (agentId, data) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.connectNotion(agentId, data);
      await loadKnowledge(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao conectar Notion';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadKnowledge]);

  /**
   * Connect Google Drive
   */
  const connectGoogleDrive = useCallback(async (agentId, data) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.connectGoogleDrive(agentId, data);
      await loadKnowledge(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao conectar Google Drive';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadKnowledge]);

  // ===============================================
  // INTEGRATIONS
  // ===============================================

  /**
   * Load integrations
   */
  const loadIntegrations = useCallback(async (agentId) => {
    try {
      const response = await AgentsFlowService.getIntegrations(agentId);
      setEditorData({ integrations: response.data || [] });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar integrações';
      setError(errorMsg);
      throw error;
    }
  }, [setEditorData, setError]);

  /**
   * Connect integration
   */
  const connectIntegration = useCallback(async (agentId, integrationData) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.connectIntegration(agentId, integrationData);
      await loadIntegrations(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao conectar integração';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadIntegrations]);

  /**
   * Disconnect integration
   */
  const disconnectIntegration = useCallback(async (agentId, integrationId) => {
    try {
      await AgentsFlowService.disconnectIntegration(agentId, integrationId);
      await loadIntegrations(agentId);
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao desconectar integração';
      setError(errorMsg);
      throw error;
    }
  }, [setError, loadIntegrations]);

  /**
   * Update integration configuration
   */
  const updateIntegration = useCallback(async (agentId, integrationId, data) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.updateIntegration(agentId, integrationId, data);
      await loadIntegrations(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao atualizar integração';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadIntegrations]);

  /**
   * Test integration connection
   */
  const testIntegration = useCallback(async (agentId, integrationId) => {
    try {
      const response = await AgentsFlowService.testIntegration(agentId, integrationId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao testar integração';
      setError(errorMsg);
      throw error;
    }
  }, [setError]);

  // ===============================================
  // GOOGLE OAUTH
  // ===============================================

  /**
   * Get Google OAuth authorization URL
   */
  const getGoogleAuthUrl = useCallback(async (agentId, services = ['calendar', 'drive', 'gmail', 'docs']) => {
    try {
      const redirectUri = `${window.location.origin}/agents-flow/${agentId}/oauth/callback`;
      const response = await AgentsFlowService.getGoogleAuthUrl(agentId, redirectUri, services);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao obter URL de autorização';
      setError(errorMsg);
      throw error;
    }
  }, [setError]);

  /**
   * Check Google integration status
   */
  const checkGoogleStatus = useCallback(async (agentId) => {
    try {
      const response = await AgentsFlowService.checkGoogleStatus(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao verificar status do Google';
      setError(errorMsg);
      throw error;
    }
  }, [setError]);

  /**
   * Disconnect Google integration
   */
  const disconnectGoogle = useCallback(async (agentId) => {
    try {
      const response = await AgentsFlowService.disconnectGoogle(agentId);
      await loadIntegrations(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao desconectar Google';
      setError(errorMsg);
      throw error;
    }
  }, [setError, loadIntegrations]);

  // ===============================================
  // TEMPLATES
  // ===============================================

  /**
   * Load templates
   */
  const loadTemplates = useCallback(async (params = {}) => {
    setTemplatesLoading(true);
    try {
      const response = await AgentsFlowService.listTemplates(params);
      setTemplates(response.data?.results || response.data || []);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar templates';
      setError(errorMsg);
      throw error;
    } finally {
      setTemplatesLoading(false);
    }
  }, [setTemplates, setTemplatesLoading, setError]);

  /**
   * Create from template
   */
  const createFromTemplate = useCallback(async (templateId, customizations = {}) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.createFromTemplate(templateId, customizations);
      addAgent(response.data);
      setCurrentAgent(response.data);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao criar a partir do template';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [addAgent, setCurrentAgent, setSaving, setError]);

  // ===============================================
  // SIMULATOR
  // ===============================================

  /**
   * Process agent response (humanized or single message)
   */
  const handleAgentResponse = useCallback(async (responseData, debugMode = false) => {
    const humanizedMessages = responseData?.messages || [];
    const debugTrace = debugMode ? responseData?.debug_trace : null;

    if (humanizedMessages.length > 0) {
      for (let i = 0; i < humanizedMessages.length; i++) {
        const msg = humanizedMessages[i];
        const delayMs = i > 0 ? (msg.delay_before_ms || 500) : 0;
        const isLastMessage = i === humanizedMessages.length - 1;

        if (delayMs > 0) {
          await new Promise(resolve => setTimeout(resolve, Math.min(delayMs, 3000)));
        }

        addSimulatorMessage({
          id: Date.now() + i + 1,
          type: 'agent',
          content: msg.text || '',
          timestamp: new Date().toISOString(),
          metadata: {
            intent: i === 0 ? responseData?.intent : undefined,
            capabilities_triggered: i === 0 ? responseData?.capabilities_triggered : undefined,
            collected_data: isLastMessage ? responseData?.collected_data : undefined,
            should_transfer: isLastMessage ? responseData?.should_transfer : undefined,
            humanized: true,
            message_index: i,
            total_messages: humanizedMessages.length,
            debug_trace: isLastMessage ? debugTrace : undefined,
          },
        });
      }
    } else {
      addSimulatorMessage({
        id: Date.now() + 1,
        type: 'agent',
        content: responseData?.agent_response || responseData?.response || responseData?.message || '',
        timestamp: new Date().toISOString(),
        metadata: {
          intent: responseData?.intent,
          capabilities_triggered: responseData?.capabilities_triggered,
          collected_data: responseData?.collected_data,
          should_transfer: responseData?.should_transfer,
          debug_trace: debugTrace,
        },
      });
    }
  }, [addSimulatorMessage]);

  /**
   * Send test message
   */
  const sendTestMessage = useCallback(async (agentId, message, debugMode = false) => {
    addSimulatorMessage({
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    });

    setSimulatorTyping(true);

    try {
      // debug=true sempre — habilita debug_trace, api_responses e
      // declared_variables no response (para painel de variáveis do
      // playground). Custo: varredura O(n) do graph_definition + um pouco
      // mais de payload no JSON. Aceitável em ambiente de teste.
      const payload = { message, context: { debug: true } };
      const response = await AgentsFlowService.testAgent(agentId, payload);
      await handleAgentResponse(response.data, debugMode);
      return response.data;
    } catch (error) {
      addSimulatorMessage({
        id: Date.now() + 1,
        type: 'error',
        content: 'Erro ao processar mensagem. Tente novamente.',
        timestamp: new Date().toISOString(),
      });
      throw error;
    } finally {
      setSimulatorTyping(false);
    }
  }, [addSimulatorMessage, setSimulatorTyping, handleAgentResponse]);

  /**
   * Send test image
   */
  const sendTestImage = useCallback(async (agentId, imageFile) => {
    const previewUrl = URL.createObjectURL(imageFile);
    addSimulatorMessage({
      id: Date.now(),
      type: 'user',
      content: JSON.stringify({
        type: 'media',
        media_type: 'image',
        url: previewUrl,
        filename: imageFile.name,
      }),
      timestamp: new Date().toISOString(),
    });

    setSimulatorTyping(true);

    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      // context.debug=true habilita debug_trace, api_responses e
      // declared_variables no response — necessário para o painel
      // de variáveis. Sem isso, o painel não atualiza após envio de
      // mídia (declared_variables vem [] e o hook nunca popula o
      // esqueleto). Bug observado em 2026-05-18 no Dedé.
      formData.append('context', JSON.stringify({ debug: true }));
      const response = await AgentsFlowService.testAgentMedia(agentId, formData);
      await handleAgentResponse(response.data);
      return response.data;
    } catch (error) {
      addSimulatorMessage({
        id: Date.now() + 1,
        type: 'error',
        content: 'Erro ao enviar imagem. Tente novamente.',
        timestamp: new Date().toISOString(),
      });
      throw error;
    } finally {
      setSimulatorTyping(false);
    }
  }, [addSimulatorMessage, setSimulatorTyping, handleAgentResponse]);

  /**
   * Send test audio
   */
  const sendTestAudio = useCallback(async (agentId, audioBlob) => {
    const previewUrl = URL.createObjectURL(audioBlob);
    addSimulatorMessage({
      id: Date.now(),
      type: 'user',
      content: JSON.stringify({
        type: 'media',
        media_type: 'audio',
        url: previewUrl,
        filename: 'audio.webm',
      }),
      timestamp: new Date().toISOString(),
    });

    setSimulatorTyping(true);

    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      // context.debug=true — habilita declared_variables no response.
      // Mesmo bug de 2026-05-18 que afetava envio de imagens.
      formData.append('context', JSON.stringify({ debug: true }));
      const response = await AgentsFlowService.testAgentMedia(agentId, formData);
      await handleAgentResponse(response.data);
      return response.data;
    } catch (error) {
      addSimulatorMessage({
        id: Date.now() + 1,
        type: 'error',
        content: 'Erro ao enviar áudio. Tente novamente.',
        timestamp: new Date().toISOString(),
      });
      throw error;
    } finally {
      setSimulatorTyping(false);
    }
  }, [addSimulatorMessage, setSimulatorTyping, handleAgentResponse]);

  // ===============================================
  // MESSAGES & FAQ
  // ===============================================

  /**
   * Load messages
   */
  const loadMessages = useCallback(async (agentId) => {
    try {
      const response = await AgentsFlowService.getMessages(agentId);
      setEditorData({ messages: response.data || {} });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar mensagens';
      setError(errorMsg);
      throw error;
    }
  }, [setEditorData, setError]);

  /**
   * Save messages
   */
  const saveMessages = useCallback(async (agentId, messages) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.updateMessages(agentId, messages);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao salvar mensagens';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError]);

  /**
   * Load FAQ
   */
  const loadFAQ = useCallback(async (agentId) => {
    try {
      const response = await AgentsFlowService.getFAQ(agentId);
      setEditorData({ faq: response.data || [] });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar FAQ';
      setError(errorMsg);
      throw error;
    }
  }, [setEditorData, setError]);

  /**
   * Add FAQ entry
   */
  const addFAQEntry = useCallback(async (agentId, entry) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.addFAQEntry(agentId, entry);
      await loadFAQ(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao adicionar FAQ';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadFAQ]);

  /**
   * Delete FAQ entry
   */
  const deleteFAQEntry = useCallback(async (agentId, faqId) => {
    try {
      await AgentsFlowService.deleteFAQEntry(agentId, faqId);
      await loadFAQ(agentId);
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao excluir FAQ';
      setError(errorMsg);
      throw error;
    }
  }, [setError, loadFAQ]);

  // ===============================================
  // LLM CONFIGURATION
  // ===============================================

  /**
   * Load LLM configuration
   */
  const loadLLMConfig = useCallback(async (agentId) => {
    try {
      const response = await AgentsFlowService.getLLMConfig(agentId);
      setEditorData({ llm_config: response.data || {} });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar configuração LLM';
      setError(errorMsg);
      throw error;
    }
  }, [setEditorData, setError]);

  /**
   * Save LLM configuration
   */
  const saveLLMConfig = useCallback(async (agentId, config) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.updateLLMConfig(agentId, config);
      setEditorData({ llm_config: response.data || config });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao salvar configuração LLM';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setEditorData, setError]);

  // ===============================================
  // ADVANCED CONFIGURATION
  // ===============================================

  /**
   * Load advanced configuration
   */
  const loadAdvancedConfig = useCallback(async (agentId) => {
    try {
      const response = await AgentsFlowService.getAdvancedConfig(agentId);
      setEditorData({ advanced_config: response.data || {} });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar configuração avançada';
      setError(errorMsg);
      throw error;
    }
  }, [setEditorData, setError]);

  /**
   * Save advanced configuration
   */
  const saveAdvancedConfig = useCallback(async (agentId, config) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.updateAdvancedConfig(agentId, config);
      setEditorData({ advanced_config: response.data || config });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao salvar configuração avançada';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setEditorData, setError]);

  // ===============================================
  // PRODUCTS
  // ===============================================

  /**
   * Load products
   */
  const loadProducts = useCallback(async (agentId, params = {}) => {
    try {
      const response = await AgentsFlowService.getProducts(agentId, params);
      setEditorData({ products: response.data?.results || response.data || [] });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar produtos';
      setError(errorMsg);
      throw error;
    }
  }, [setEditorData, setError]);

  /**
   * Add product
   */
  const addProduct = useCallback(async (agentId, data) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.addProduct(agentId, data);
      await loadProducts(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao adicionar produto';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadProducts]);

  /**
   * Update product
   */
  const updateProduct = useCallback(async (agentId, productId, data) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.updateProduct(agentId, productId, data);
      await loadProducts(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao atualizar produto';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadProducts]);

  /**
   * Delete product
   */
  const deleteProduct = useCallback(async (agentId, productId) => {
    try {
      await AgentsFlowService.deleteProduct(agentId, productId);
      await loadProducts(agentId);
      return true;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao excluir produto';
      setError(errorMsg);
      throw error;
    }
  }, [setError, loadProducts]);

  /**
   * Import products from file
   */
  const importProducts = useCallback(async (agentId, file) => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await AgentsFlowService.importProducts(agentId, formData);
      await loadProducts(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao importar produtos';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadProducts]);

  /**
   * Import products from Google Sheets
   */
  const importProductsFromSheets = useCallback(async (agentId, data) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.importProductsFromSheets(agentId, data);
      await loadProducts(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao importar produtos do Google Sheets';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadProducts]);

  /**
   * Sync products
   */
  const syncProducts = useCallback(async (agentId) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.syncProducts(agentId);
      await loadProducts(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao sincronizar produtos';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError, loadProducts]);

  // ===============================================
  // NOTIFICATION RULES
  // ===============================================

  const loadNotificationRules = useCallback(async (agentId) => {
    try {
      const response = await AgentsFlowService.getNotificationRules(agentId);
      setEditorData({ notification_rules: response.data || [] });
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar regras de notificação';
      setError(errorMsg);
      throw error;
    }
  }, [setEditorData, setError]);

  const createNotificationRule = useCallback(async (agentId, data) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.createNotificationRule(agentId, data);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao criar regra de notificação';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError]);

  const updateNotificationRule = useCallback(async (agentId, ruleId, data) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.updateNotificationRule(agentId, ruleId, data);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao atualizar regra de notificação';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError]);

  const deleteNotificationRule = useCallback(async (agentId, ruleId) => {
    try {
      await AgentsFlowService.deleteNotificationRule(agentId, ruleId);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao excluir regra de notificação';
      setError(errorMsg);
      throw error;
    }
  }, [setError]);

  // ===============================================
  // DAILY SUMMARY
  // ===============================================

  const loadDailySummaryConfig = useCallback(async () => {
    try {
      const response = await AgentsFlowService.getDailySummaryConfig();
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar configuração de resumo diário';
      setError(errorMsg);
      throw error;
    }
  }, [setError]);

  const saveDailySummaryConfig = useCallback(async (data) => {
    setSaving(true);
    try {
      const response = await AgentsFlowService.updateDailySummaryConfig(data);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao salvar configuração de resumo diário';
      setError(errorMsg);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [setSaving, setError]);

  const loadAvailableSummaryModules = useCallback(async () => {
    try {
      const response = await AgentsFlowService.getAvailableSummaryModules();
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao carregar módulos de resumo';
      setError(errorMsg);
      throw error;
    }
  }, [setError]);

  // ===============================================
  // MEMORY MANAGEMENT
  // ===============================================

  /**
   * Reset agent memory and conversation context
   */
  const resetMemory = useCallback(async (agentId) => {
    try {
      const response = await AgentsFlowService.resetMemory(agentId);
      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erro ao limpar memória do agente';
      setError(errorMsg);
      throw error;
    }
  }, [setError]);

  // ===============================================
  // PLAYGROUND TEST SESSIONS
  // ===============================================

  const listPlaygroundSessions = useCallback(async (agentId) => {
    const response = await AgentsFlowService.listPlaygroundSessions(agentId);
    return response.data;
  }, []);

  // Arquiva sessão atual. NÃO captura HTTP 409 — caller precisa tratar
  // (mostra modal de gerenciamento das 10 sessões já existentes).
  const archivePlaygroundSession = useCallback(async (agentId, payload) => {
    const response = await AgentsFlowService.archivePlaygroundSession(agentId, payload);
    return response.data;
  }, []);

  const getPlaygroundSession = useCallback(async (agentId, sessionId) => {
    const response = await AgentsFlowService.getPlaygroundSession(agentId, sessionId);
    return response.data;
  }, []);

  const deletePlaygroundSession = useCallback(async (agentId, sessionId) => {
    await AgentsFlowService.deletePlaygroundSession(agentId, sessionId);
  }, []);

  const clearAllPlaygroundSessions = useCallback(async (agentId) => {
    const response = await AgentsFlowService.clearAllPlaygroundSessions(agentId);
    return response.data;
  }, []);

  // ===============================================
  // RETURN
  // ===============================================

  return {
    // Agents
    loadAgents,
    loadAgent,
    loadAgentById: loadAgent, // Alias for canvas
    createAgent,
    quickCreateAgent,
    updateAgent: updateAgentApi,
    deleteAgent,
    publishAgent,
    // Capabilities
    loadCapabilities,
    toggleCapabilities,
    updateCapabilityConfig,
    // Knowledge
    loadKnowledge,
    uploadDocument,
    deleteKnowledge,
    syncKnowledge,
    addWebsiteKnowledge,
    connectGoogleSheets,
    connectNotion,
    connectGoogleDrive,
    // Integrations
    loadIntegrations,
    connectIntegration,
    disconnectIntegration,
    updateIntegration,
    testIntegration,
    // Google OAuth
    getGoogleAuthUrl,
    checkGoogleStatus,
    disconnectGoogle,
    // Templates
    loadTemplates,
    createFromTemplate,
    // Simulator
    sendTestMessage,
    sendTestImage,
    sendTestAudio,
    // Messages & FAQ
    loadMessages,
    saveMessages,
    loadFAQ,
    addFAQEntry,
    deleteFAQEntry,
    // LLM Config
    loadLLMConfig,
    saveLLMConfig,
    // Advanced Config
    loadAdvancedConfig,
    saveAdvancedConfig,
    // Products
    loadProducts,
    addProduct,
    updateProduct,
    deleteProduct,
    importProducts,
    importProductsFromSheets,
    syncProducts,
    // Notification Rules
    loadNotificationRules,
    createNotificationRule,
    updateNotificationRule,
    deleteNotificationRule,
    // Daily Summary
    loadDailySummaryConfig,
    saveDailySummaryConfig,
    loadAvailableSummaryModules,
    // Memory
    resetMemory,
    // Playground Sessions
    listPlaygroundSessions,
    archivePlaygroundSession,
    getPlaygroundSession,
    deletePlaygroundSession,
    clearAllPlaygroundSessions,
  };
}

export default useAgentsFlowApi;
